import React, { FC, useCallback, useMemo, useState } from "react";
import { Switch, Tooltip, Typography } from "antd";
import styles from "./LidarBottomWrapper.module.less";
import LidarBottomMesh from "./components/lidarBottomMesh/LidarBottomMesh";
import LidarBottomScatter from "./components/lidarBottomScatter/LidarBottomScatter";
import { LaserScanParameterModel } from "~/pages/copilotMessage/types/copilot";
import { getFullScreenChartSubTitle } from "../../../../../../helpers/chartHelper";
import HeatSlider, {
  HeatAndGlobalIndex,
} from "../../../../../heatSlider/HeatSlider";
import { useLiDARHeatData } from "../../../../hooks/useLiDARHeatData";
import { LidarDataSummaryForVisualizationMapped } from "../../../../types/getLiDARDataVisualizationSummariesForLastSpecificHeats";
import { NoDataAvailable } from "~/components/noDataAvailable/NoDataAvailable";
import {
  CaretLeftOutlined,
  CaretRightOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { MouseAndKeyboardTutorial2D } from "~/components/2DTutorial/MouseAndKeyboardTutorial2D";

interface LidarBottomWrapperProps {
  data: LidarDataSummaryForVisualizationMapped[];
  isExpanded: boolean;
  lidarParams: LaserScanParameterModel;
  currentHeatInfoRef?: React.MutableRefObject<HTMLSpanElement | null>;
}

const LidarBottomWrapper: FC<LidarBottomWrapperProps> = ({
  data,
  isExpanded,
  lidarParams,
  currentHeatInfoRef,
}) => {
  const [isMeshDisplay, setIsMeshDisplay] = useState<boolean>(true);
  const { heatTexts, defaultIndex } = useLiDARHeatData(data, lidarParams);

  const [selectedIndex, setSelectedIndex] = useState<HeatAndGlobalIndex>();

  const sliderRef = React.createRef<{
    onClickPrevious: () => void;
    onClickNext: () => void;
  }>();

  const bottomScatterCompKey = useMemo(
    () =>
      data
        .map((x) => x.liDARDataId)
        .join(",")
        .concat(isExpanded.toString()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data.length, isExpanded],
  );

  const handleSwitch = useCallback(() => {
    setIsMeshDisplay((prev) => !prev);
  }, []);

  const changeMeshData = useCallback(
    (index?: HeatAndGlobalIndex) => {
      if (currentHeatInfoRef?.current && index?.heatIndex != undefined) {
        currentHeatInfoRef!.current!.innerHTML = getFullScreenChartSubTitle(
          heatTexts[index.heatIndex],
          data[index.heatIndex].heatName,
        );
      } else if (currentHeatInfoRef?.current && index == undefined) {
        currentHeatInfoRef!.current!.innerHTML = "-";
      }
      setSelectedIndex(index);
    },
    [currentHeatInfoRef, data, heatTexts],
  );
  const MemoizedLidarBottom = useMemo(
    () => {
      if (selectedIndex?.heatIndex == undefined) {
        return (
          <NoDataAvailable
            style={{ width: "100%" }}
            title={"No Laser Scan"}
            text={"for this heat"}
          />
        );
      }
      const visualizationZoneData =
        data[selectedIndex.heatIndex].visualizationZoneMappedData;

      return isMeshDisplay ? (
        <LidarBottomMesh
          key={bottomScatterCompKey}
          visualizationZoneData={visualizationZoneData}
          isExpand={isExpanded}
        />
      ) : (
        <LidarBottomScatter
          key={bottomScatterCompKey}
          visualizationZoneData={visualizationZoneData}
          isExpanded={isExpanded}
        />
      );
    },
    //INFO: data is a huge array and it is not necessary to check it for changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      bottomScatterCompKey,
      selectedIndex,
      isExpanded,
      lidarParams,
      currentHeatInfoRef,
      isMeshDisplay,
    ],
  );

  return (
    <>
      <div className={styles.bottomContainer}>
        <div className={styles.meshContainer}>
          {isExpanded && heatTexts.length > 1 && (
            <div
              style={{
                visibility:
                  selectedIndex?.globalIndex !== 0 ? "unset" : "hidden",
              }}
              className={styles.heatNavigationContainer}
              onClick={() => {
                sliderRef.current?.onClickPrevious();
              }}
            >
              <CaretLeftOutlined className={styles.navigationIcon} />
              <span
                className={styles.navigationText}
              >{`Previous Laser \n Scan`}</span>
            </div>
          )}
          <Typography.Title
            level={isExpanded ? 1 : 2}
            className={isExpanded ? styles.expandedHeatText : styles.heatText}
          >
            {selectedIndex?.heatIndex != undefined
              ? `Heat ${heatTexts[selectedIndex.heatIndex]}`
              : ""}
          </Typography.Title>
          {MemoizedLidarBottom}
          {isExpanded && heatTexts.length > 1 && (
            <div
              style={{
                visibility:
                  (selectedIndex?.heatIndex || 0) < data.length - 1
                    ? "unset"
                    : "hidden",
              }}
              className={styles.heatNavigationContainer}
              onClick={() => {
                sliderRef.current?.onClickNext();
              }}
            >
              <CaretRightOutlined className={styles.navigationIcon} />
              <span
                className={styles.navigationText}
              >{`Next Laser \n Scan`}</span>
            </div>
          )}
        </div>
      </div>

      {isExpanded && (
        <Tooltip
          overlay={<MouseAndKeyboardTutorial2D />}
          placement="topRight"
          fresh
          overlayInnerStyle={{ width: "300px" }}
          getPopupContainer={(trigger) =>
            trigger.parentElement || document.body
          }
        >
          <InfoCircleOutlined className={styles.infoButton} />
        </Tooltip>
      )}

      <Switch
        className={
          isExpanded
            ? styles.bottomDisplayTypeSwitchExpanded
            : styles.bottomDisplayTypeSwitch
        }
        checkedChildren={
          <Typography.Title level={3} style={{ color: "#fff" }}>
            mesh
          </Typography.Title>
        }
        unCheckedChildren={
          <Typography.Title level={3} style={{ color: "#fff" }}>
            points
          </Typography.Title>
        }
        checked={isMeshDisplay}
        onChange={handleSwitch}
      />
      <div className={styles.sliderWrapper}>
        <HeatSlider
          ref={sliderRef}
          key={"bottom_" + heatTexts.join(",")}
          heats={heatTexts}
          handleHeatChange={changeMeshData}
          defaultValue={defaultIndex}
          isExpanded={isExpanded}
        />
      </div>
    </>
  );
};

export default LidarBottomWrapper;
